import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, ClipboardList,
  Ticket, Calendar, Users, BarChart3, Settings,
  Wifi, LogOut, ShieldCheck, HardHat, Sparkles, Database, Network
} from 'lucide-react'
import { ROUTES } from '@/constants'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/utils/cn'

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { path: ROUTES.DASHBOARD, icon: LayoutDashboard, label: 'Dashboard' },
      { path: ROUTES.TASKS,     icon: ClipboardList,   label: 'Docket Requests' },
    ]
  },
  {
    label: 'Customer',
    items: [
      { path: ROUTES.CUSTOMERS,         icon: Database, label: 'BSNL Customers',   adminOnly: true },
      { path: ROUTES.NETWORK_TOPOLOGY,  icon: Network,  label: 'Network Topology', adminOnly: true }
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: ROUTES.TICKETS,   icon: Ticket,    label: 'Tickets' },
      { path: ROUTES.FOLLOWUPS, icon: Calendar,  label: 'Follow-ups' },
    ]
  },
  {
    label: 'Management',
    items: [
      { path: ROUTES.ENGINEERS, icon: Users,    label: 'Users',    adminOnly: false },
      { path: ROUTES.REPORTS,   icon: BarChart3, label: 'Reports', adminOnly: true },
      { path: ROUTES.SETTINGS,  icon: Settings,  label: 'Settings', adminOnly: false },
    ]
  }
]

const Sidebar = () => {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'admin'

  const handleLogout = () => { logout(); navigate(ROUTES.LOGIN) }

  return (
    <aside className="hidden lg:flex flex-col w-64 min-h-screen shrink-0 fixed top-0 left-0 bottom-0 z-40"
      style={{ background: 'linear-gradient(180deg, #080f1e 0%, #0d1b33 50%, #0a1628 100%)' }}>

      {/* Decorative top glow */}
      <div className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(59,130,246,0.18) 0%, transparent 70%)' }} />

      {/* Logo */}
      <div className="relative z-10 flex items-center gap-3 px-5 py-5">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 animate-breathe"
          style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)', boxShadow: '0 0 20px rgba(59,130,246,0.4)' }}>
          <Wifi size={19} className="text-white" />
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <p className="font-extrabold text-white text-sm tracking-wide">BSNL FTTH</p>
            <Sparkles size={11} className="text-cyan-400" />
          </div>
          <p className="text-[10px] text-blue-300/60 font-medium">Support Platform</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="relative z-10 flex-1 overflow-y-auto scrollbar-hide px-3 pb-3 space-y-5">
        {NAV_SECTIONS.map((section, si) => {
          const visible = section.items.filter(i => !('adminOnly' in i && i.adminOnly && !isAdmin))
          if (!visible.length) return null
          return (
            <div key={section.label}>
              {si > 0 && (
                <div className="mx-3 mb-3 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.18), transparent)' }} />
              )}
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-blue-400/40 px-3 mb-2">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {visible.map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === ROUTES.DASHBOARD}
                    className={({ isActive }) => cn(
                      'relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group overflow-hidden',
                      isActive
                        ? 'text-white'
                        : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
                    )}
                  >
                    {({ isActive }) => (
                      <>
                        {/* Active background */}
                        {isActive && (
                          <span className="absolute inset-0 rounded-xl"
                            style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.35) 0%, rgba(6,182,212,0.2) 100%)', border: '1px solid rgba(59,130,246,0.3)' }} />
                        )}
                        {/* Left indicator bar */}
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full bg-blue-400 transition-all duration-300" />
                        )}
                        <item.icon
                          size={16}
                          className={cn(
                            'relative z-10 shrink-0 transition-colors',
                            isActive ? 'text-blue-300' : 'text-slate-500 group-hover:text-slate-300'
                          )}
                        />
                        <span className="relative z-10 flex-1">{item.label}</span>
                        {isActive && (
                          <span className="relative z-10 w-1.5 h-1.5 rounded-full bg-blue-400" />
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          )
        })}
      </nav>

      {/* Divider */}
      <div className="mx-4 border-t border-white/[0.07]" />

      {/* User card */}
      <div className="relative z-10 p-3">
        <div
          className="flex items-center gap-3 p-3 rounded-2xl cursor-pointer transition-all duration-200 group"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
          onClick={() => navigate(ROUTES.PROFILE)}
        >
          {/* Avatar */}
          <div className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center text-sm font-black text-white"
            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)' }}>
            {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name ?? 'User'}</p>
            <div className="flex items-center gap-1 mt-0.5">
              {isAdmin
                ? <ShieldCheck size={10} className="text-blue-400" />
                : <HardHat size={10} className="text-slate-400" />}
              <p className="text-[10px] text-slate-400">
                {isAdmin ? 'Administrator' : 'Field Engineer'}
              </p>
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); handleLogout() }}
            title="Logout"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut size={13} />
          </button>
        </div>
      </div>
    </aside>
  )
}

export default Sidebar
